import { Component, ElementRef, EventEmitter, Input, Output, Type, ViewChild, inject } from '@angular/core';
import { AutoComplete } from 'primeng/autocomplete';
import { Dropdown } from 'primeng/dropdown';
import { Fieldset } from 'primeng/fieldset';
import { InputNumber } from 'primeng/inputnumber';
import { MultiSelect } from 'primeng/multiselect';
import { MainAppService } from 'src/modules/app-template/services/main-app.service';
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { Utils } from 'src/modules/utils/shared/utils';
import { InsertionPointDirective } from '../../directives/insertion-point.directive';
import { FrontendFieldDefinition } from '../../models/frontend-field-definition.model';
import { FrontendFieldType } from '../../models/frontend-field-type.enum';
import { FrontendFormDefinition } from '../../models/frontend-form-definition.model';
import { TableData } from '../../models/table-data.model';
import { TableRow } from '../../models/table-row.model';
import { SmTable2Component } from '../sm-table2/sm-table2.component';

@Component({
    selector: 'app-sm-form-field-internal',
    templateUrl: './sm-form-field-internal.component.html',
    styleUrls: ['./sm-form-field-internal.component.scss']
})
export class SmFormFieldInternalComponent {

    _FrontendFieldType = FrontendFieldType;
    _Utils = Utils;

    app = inject(MainAppService);
    elementRef = inject(ElementRef);

    errorMessage = "";
    fixedPos = false;
    tableData: TableData;
    tableDataIsForValue: any[];

    @Input()
    form: FrontendFormDefinition;
    @Input()
    fluentWidth: string = null;

    mainRadioButtonField: FrontendFieldDefinition;

    @Output()
    onClick = new EventEmitter<FrontendFieldDefinition>();

    private _field: FrontendFieldDefinition = null;
    private valueBeforeEdit: any;

    autoCompleteSuggestions = [];

    pageChildren: FrontendFieldDefinition[][][] = [];

    _mustRefresh = this.mustRefresh.bind(this);

    @ViewChild(InsertionPointDirective, {static: true}) insertionPoint: InsertionPointDirective;

    @ViewChild("componentFieldSet") componentFieldSet: Fieldset;
    @ViewChild("componentInputText") componentInputText: any;
    @ViewChild("componentInputNumber") componentInputNumber: InputNumber;
    @ViewChild("componentMultiSelect") componentMultiSelect: MultiSelect;
    @ViewChild("componentAutoComplete") componentAutoComplete: AutoComplete;
    @ViewChild("componentDropdown") componentDropdown: Dropdown;
    @ViewChild("componentTable") componentTable: SmTable2Component;

    _addTableRow = this.addTableRow.bind(this);
    _removeTableRow = this.removeTableRow.bind(this);
    _setTableRows = this.setTableRows.bind(this);

    ngAfterViewInit(): void {
        let ne = this.componentAutoComplete?.el?.nativeElement;
        if (ne != null) {
            //Bei einem AutoComplete wollen wir nicht, dass die Tab Taste zum Dropdown-Pfeil-Button springt, sondern direkt zum nächsten Control
            ne.querySelector('.p-autocomplete-dropdown')?.setAttribute('tabindex', '-1');
        }
    }

    ngDoCheck(_): void {
        this.errorMessage = this.field?.getErrorMessage(true, true);
        this.fixedPos = this.field?.isFixedPosition();
        if (this.field?.type == FrontendFieldType.table) {
            if (this.tableData == null || this.tableDataIsForValue != this.field.value || this.tableDataIsForValue != null && this._field.value != null && this.tableDataIsForValue.length != this._field.value.length || this.tableData.columns?.length != this.field.tableColumns?.length) {
                this.tableData = this.field.value != null ? new TableData(this.field.tableColumns, (this.field.value as any[]).map(row => new TableRow("", row, row as OrdinaryObject))) : null;
                this.tableDataIsForValue = this.field.value;
            }
        }
        else {
            this.tableData = null;
        }
        if (this.field.tableSelectedRow != this.field.tableSelectedRowRequest) {
            this.tableSetSelectedRowIndex(this.field.tableSelectedRowRequest);
        }
        if (this.field.type == FrontendFieldType.radioButton) {
            this.mainRadioButtonField = this.form.getAllFields().find(f => f.radioButtonGroup == this.field.radioButtonGroup && f.radioButtonValue == 0);    
        }   

        if (this.field.requestFocus) {
            this.field.requestFocus = false;
            this.focus();
        }
        
    }

    @Input()
    set field(value: FrontendFieldDefinition) {
        this._field = value;

        if (this.field.type == FrontendFieldType.custom) {
            this._field.component = this.app.createComponent(this.insertionPoint, this._field.componentType as Type<any>);
            this._field.component.value = this._field.value;
            this._field.component.tableCellEditor = this._field.tableCellEditor;
           /* this._field.component.valueChange.subscribe(newValue => {
                this.valueChanged(true);
            });
          */  this._field.component.setContext(this._field.context);
        }
        this.pageChildren = this.field?.type == FrontendFieldType.pageControl ? this.field.children.map(page => page.getGroupedChildren()) : [];
    }

    get field(): FrontendFieldDefinition {
        return this._field;
    }

    buttonClick(event: MouseEvent): void {
        //Aus unbekannten Gründen wird dieser Eventhandler auch aufgerufen, wenn in einem beliebigen Formfeld Enter gedrückt wird.
        //Dann sind screenX und screenY = 0. Solche "Klicks" werden ignoriert.
        if (event.screenX === 0 && event.screenY === 0) {
            return;
        }
        if (this._field.onClick != null) {
            this._field.onClick(event);
        }
    }

    mustRefresh(): boolean {
        if (this._field == null) {
            return false;
        }
        let result = this._field.tableMustRefresh;
        this._field.tableMustRefresh = false;
        return result;
    }

    valueChanged(byUser = true): void {
        let f = this.mainRadioButtonField ?? this._field;
        if (f.onValueChanged != null) {
            f.onValueChanged(byUser);
        }
    }

    addTableRow(): void {
        if (this._field.onBeforeAddRow != null) {
            this._field.onBeforeAddRow();
        }
        this._field.value = [...(this._field.value as object[]), this._field.generateEmptyTableItem()];
        this.valueChanged();
    }

    removeTableRow(row: TableRow, rowIndex: number): void {
        this._field.value.splice(rowIndex, 1);
        this._field.value = [...this._field.value];
        this.valueChanged();
    }

    setTableRows(rows: TableRow[]): void {
        this._field.value = rows.map(row => row.values);
        this.valueChanged();
    }

    deleteTableRow(rowIndex: number): void {
        (this._field.value as object[]).splice(rowIndex, 1);
        this.valueChanged();
        //Das wird benötigt, um die Change Detection zu triggern. Sonst stimmen Breiten von Header und Zeilen nicht überein
        this._field.value = [...(this._field.value as object[])];
    }

    async editTableRow(rowIndex: number): Promise<void> {
        let form = new FrontendFormDefinition(this._field.tableColumns.map(col => col.toFrontendField()));
        form.fill((this._field.value as object[])[rowIndex]);
        await this.app.messageDialog.form(form, "Zeile editieren");
    }

    tableSelectRow(row: TableRow): void {
        this.field.tableSelectedRow = this.tableData.rows.findIndex(row2 => row2 == row);
        this.field.tableSelectedRowRequest = this.field.tableSelectedRow;
    }

    tableSetSelectedRowIndex(index: number): void {
        this.field.tableSelectedRow = index;
        this.field.tableSelectedRowRequest = index;
        if (this.componentTable != null) {
            this.componentTable.setSelectedRowIndex(index);
        }
    }

    tableEditInit(): void {
        this.valueBeforeEdit = Utils.cloneDeep(this.field.value);
    }

    tableEditComplete(): void {
        if (this._field.onValueChanged != null && !Utils.equalsDeep(this.valueBeforeEdit, this.field.value)) {
            this._field.onValueChanged(true);
        }
    }

    focus(): void {
        this.componentInputText?.nativeElement?.focus();
        this.componentInputNumber?.el?.nativeElement?.focus();
        this.componentMultiSelect?.containerViewChild?.nativeElement?.querySelector('input')?.focus();
        this.componentMultiSelect?.el?.nativeElement?.focus();
        this.componentAutoComplete?.el?.nativeElement?.focus();
        this.componentDropdown?.el?.nativeElement?.focus();
    }

    thisComponentAsTableEditorInited(): void {
        this.componentDropdown?.show(true);
    }

    async autoComplete(event: any): Promise<void> {
        if (this._field.onAutoComplete != null) {
            this.autoCompleteSuggestions = await this._field.onAutoComplete(event.query);
        }
        else {
            this.autoCompleteSuggestions = this.field.listItems.filter(li => li.label != null && Utils.stringIncludesCi(li.label, event.query as string));
        }
    }

    getColorPalette(): string[] {
        return this.field.predefinedColors.map(c => Utils.toColorHexString(c));
    }

    colorChanged(color: string): void {
        this.field.value = Utils.toColorNumber(color);
    }

    getStyle(style: OrdinaryObject): OrdinaryObject {
        return  { ... style, ...this.field.style };
    }

}
